<template>
  <div class="dashboard-main">
    <header class="dashboard-header">
      <div class="header-content">
        <h1 class="dashboard-title">
          <i class="material-icons">dashboard</i>
          Resumen del sistema
        </h1>
        <p class="dashboard-subtitle">Estado financiero y operativo de EasyMoney</p>
        <div v-if="user" class="dashboard-user">
          <span class="user-name">{{ user.firstName }} {{ user.lastName }}</span>
          <span class="user-email">{{ user.email }}</span>
        </div>
      </div>
    </header>

    <div v-if="loading" class="dashboard-loading">
      <v-progress-circular indeterminate color="primary" size="48" />
      <p>Cargando estadísticas...</p>
    </div>

    <div v-else-if="error" class="dashboard-error">
      <v-alert type="error" variant="tonal" closable @click:close="error = null">
        {{ error }}
      </v-alert>
      <v-btn color="primary" variant="flat" @click="fetchDashboard">Reintentar</v-btn>
    </div>

    <template v-else>
      <!-- Estado financiero -->
      <section class="dashboard-section" v-if="safeStats">
        <h2 class="section-title">
          <i class="material-icons">account_balance</i>
          Estado financiero
        </h2>
        <div class="kpi-grid">
          <div class="kpi-card kpi-primary">
            <div class="kpi-icon"><i class="material-icons">savings</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ formatCurrency(safeStats.totalAmountLoans) }}</span>
              <span class="kpi-label">Cartera total (préstamos)</span>
            </div>
          </div>
          <div class="kpi-card kpi-success">
            <div class="kpi-icon"><i class="material-icons">payments</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ formatCurrency(safeStats.totalPaymentsReceived) }}</span>
              <span class="kpi-label">Ingresos por pagos</span>
            </div>
          </div>
          <div class="kpi-card kpi-info">
            <div class="kpi-icon"><i class="material-icons">trending_up</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ formatCurrency(safeStats.totalInterestReceived) }}</span>
              <span class="kpi-label">Intereses cobrados</span>
            </div>
          </div>
          <div class="kpi-card kpi-warning">
            <div class="kpi-icon"><i class="material-icons">pending_actions</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ formatCurrency(safeStats.capitalPending) }}</span>
              <span class="kpi-label">Capital pendiente</span>
            </div>
          </div>
          <div class="kpi-card kpi-neutral">
            <div class="kpi-icon"><i class="material-icons">receipt_long</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.paymentsCount }}</span>
              <span class="kpi-label">Pagos registrados</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Estado general del sistema -->
      <section class="dashboard-section" v-if="safeStats">
        <h2 class="section-title">
          <i class="material-icons">business</i>
          Estado general
        </h2>
        <div class="kpi-grid">
          <div class="kpi-card kpi-contracts">
            <div class="kpi-icon"><i class="material-icons">description</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.totalContracts }}</span>
              <span class="kpi-label">Contratos totales</span>
            </div>
          </div>
          <div class="kpi-card kpi-active">
            <div class="kpi-icon"><i class="material-icons">check_circle</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.activeContracts }}</span>
              <span class="kpi-label">Préstamos activos</span>
            </div>
          </div>
          <div class="kpi-card kpi-month">
            <div class="kpi-icon"><i class="material-icons">calendar_today</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.contractsThisMonth }}</span>
              <span class="kpi-label">Contratos este mes</span>
            </div>
          </div>
          <div class="kpi-card kpi-expired" :class="{ 'has-alert': (safeStats.expiredContracts || 0) > 0 }">
            <div class="kpi-icon"><i class="material-icons">warning</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.expiredContracts }}</span>
              <span class="kpi-label">Contratos vencidos</span>
            </div>
          </div>
          <div class="kpi-card kpi-clients">
            <div class="kpi-icon"><i class="material-icons">people</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.totalClients }}</span>
              <span class="kpi-label">Clientes</span>
            </div>
          </div>
          <div class="kpi-card kpi-quotes">
            <div class="kpi-icon"><i class="material-icons">request_quote</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.totalQuotesPending }}</span>
              <span class="kpi-label">Cotizaciones activas</span>
            </div>
          </div>
          <div class="kpi-card kpi-vehicles">
            <div class="kpi-icon"><i class="material-icons">directions_car</i></div>
            <div class="kpi-content">
              <span class="kpi-value">{{ safeStats.totalVehiclesActive }}</span>
              <span class="kpi-label">Vehículos en sistema</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Rankings -->
      <section class="dashboard-section rankings-section">
        <div class="rankings-grid">
          <div class="ranking-card">
            <h2 class="section-title">
              <i class="material-icons">emoji_events</i>
              Vehículos más cotizados
            </h2>
            <p class="ranking-hint">Por marca y modelo (todas las cotizaciones)</p>
            <ol class="ranking-list">
              <li
                v-for="(row, index) in vehicleRanking"
                :key="`${row.brand}-${row.model}-${index}`"
                class="ranking-item"
                :class="{ 'rank-top': index < 3 }"
              >
                <span class="rank-position">{{ index + 1 }}</span>
                <span class="rank-label">{{ row.brand }} {{ row.model }}</span>
                <span class="rank-count">{{ row.count }}</span>
              </li>
              <li v-if="vehicleRanking.length === 0" class="ranking-empty">Sin datos de cotizaciones</li>
            </ol>
          </div>
          <div class="ranking-card">
            <h2 class="section-title">
              <i class="material-icons">leaderboard</i>
              Ejecutivos con más cotizaciones
            </h2>
            <p class="ranking-hint">Asesores ordenados de mayor a menor</p>
            <ol class="ranking-list">
              <li
                v-for="(row, index) in executiveQuoteRanking"
                :key="row.ejecutivoId || index"
                class="ranking-item"
                :class="{ 'rank-top': index < 3 }"
              >
                <span class="rank-position">{{ index + 1 }}</span>
                <span class="rank-label">{{ row.ejecutivoNombre }}</span>
                <span class="rank-count">{{ row.totalCotizaciones }}</span>
              </li>
              <li v-if="executiveQuoteRanking.length === 0" class="ranking-empty">Sin cotizaciones registradas</li>
            </ol>
          </div>
        </div>
      </section>

      <!-- Contratos recientes -->
      <section class="dashboard-section">
        <div class="section-header">
          <h2 class="section-title">
            <i class="material-icons">history</i>
            Contratos recientes
          </h2>
          <router-link to="/gestion/contratos" class="section-link">
            Ver todos <i class="material-icons">arrow_forward</i>
          </router-link>
        </div>
        <div class="recent-table-wrap">
          <table class="recent-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Cliente</th>
                <th>Vehículo</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in recentContracts" :key="c.id">
                <td><strong>{{ c.folio || '—' }}</strong></td>
                <td>{{ c.clienteNombre }}</td>
                <td>{{ c.vehiculoDesc }}</td>
                <td class="amount">{{ formatCurrency(c.monto) }}</td>
                <td>
                  <span :class="getStatusBadgeClass(c.estatus)">{{ getStatusText(c.estatus) }}</span>
                </td>
                <td>{{ c.fechaCreacion }}</td>
                <td>
                  <router-link :to="'/gestion/contratos'" class="btn-link" title="Ir a contratos">
                    <i class="material-icons">visibility</i>
                  </router-link>
                </td>
              </tr>
              <tr v-if="recentContracts.length === 0">
                <td colspan="7" class="empty-row">No hay contratos recientes</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import {
  me,
  getDashboardStats,
  getDashboardRecentContracts,
  getDashboardVehicleRanking,
  getDashboardExecutiveQuoteRanking,
} from '@/actions';

function getStatusBadgeClass(status) {
  if (!status) return 'status-badge status-default';
  const normalizedStatus = String(status).toLowerCase().trim();
  const classes = {
    activo: 'status-badge status-activo',
    cancelado: 'status-badge status-cancelado',
    pagado: 'status-badge status-pagado',
    renovado: 'status-badge status-renovado',
    recuperado: 'status-badge status-recuperado',
    vencido: 'status-badge status-vencido'
  };
  return classes[normalizedStatus] || 'status-badge status-default';
}

function getStatusText(status) {
  if (!status) return 'Desconocido';
  const normalizedStatus = String(status).toLowerCase().trim();
  const texts = {
    activo: 'Activo',
    cancelado: 'Cancelado',
    pagado: 'Pagado',
    renovado: 'Renovado',
    recuperado: 'En proceso de Recuperación',
    vencido: 'Vencido'
  };
  return texts[normalizedStatus] || status;
}

export default {
  name: 'SalesDashboard',
  data() {
    return {
      user: null,
      stats: {
        totalContracts: 0,
        activeContracts: 0,
        expiredContracts: 0,
        canceledContracts: 0,
        contractsThisMonth: 0,
        totalAmountLoans: 0,
        totalClients: 0,
        totalQuotesPending: 0,
        totalVehiclesActive: 0,
        totalPaymentsReceived: 0,
        totalInterestReceived: 0,
        capitalPending: 0,
        paymentsCount: 0,
      },
      recentContracts: [],
      vehicleRanking: [],
      executiveQuoteRanking: [],
      loading: true,
      error: null,
    };
  },
  computed: {
    /** Siempre devuelve un objeto para evitar "stats is undefined" en el template */
    safeStats() {
      const s = this.stats;
      if (s && typeof s === 'object') return s;
      return {
        totalContracts: 0,
        activeContracts: 0,
        expiredContracts: 0,
        canceledContracts: 0,
        contractsThisMonth: 0,
        totalAmountLoans: 0,
        totalClients: 0,
        totalQuotesPending: 0,
        totalVehiclesActive: 0,
        totalPaymentsReceived: 0,
        totalInterestReceived: 0,
        capitalPending: 0,
        paymentsCount: 0,
      };
    },
  },
  async mounted() {
    await this.loadUser();
    await this.fetchDashboard();
  },
  methods: {
    async loadUser() {
      try {
        const res = await me();
        const userData = res.data?.data?.me;
        if (userData) this.user = userData;
      } catch {
        this.user = null;
      }
    },
    async fetchDashboard() {
      this.loading = true;
      this.error = null;
      try {
        const [statsRes, recentRes, vehicleRankRes, executiveRankRes] = await Promise.all([
          getDashboardStats(),
          getDashboardRecentContracts(8),
          getDashboardVehicleRanking(10),
          getDashboardExecutiveQuoteRanking(10),
        ]);
        const statsData = statsRes.data?.data?.dashboardStats;
        const recentData = recentRes.data?.data?.dashboardRecentContracts;
        const d = statsData && typeof statsData === 'object' ? statsData : {};
        this.stats = {
          totalContracts: d.totalContracts ?? d.total_contracts ?? 0,
          activeContracts: d.activeContracts ?? d.active_contracts ?? 0,
          expiredContracts: d.expiredContracts ?? d.expired_contracts ?? 0,
          canceledContracts: d.canceledContracts ?? d.canceled_contracts ?? 0,
          contractsThisMonth: d.contractsThisMonth ?? d.contracts_this_month ?? 0,
          totalAmountLoans: d.totalAmountLoans ?? d.total_amount_loans ?? 0,
          totalClients: d.totalClients ?? d.total_clients ?? 0,
          totalQuotesPending: d.totalQuotesPending ?? d.total_quotes_pending ?? 0,
          totalVehiclesActive: d.totalVehiclesActive ?? d.total_vehicles_active ?? 0,
          totalPaymentsReceived: d.totalPaymentsReceived ?? d.total_payments_received ?? 0,
          totalInterestReceived: d.totalInterestReceived ?? d.total_interest_received ?? 0,
          capitalPending: d.capitalPending ?? d.capital_pending ?? 0,
          paymentsCount: d.paymentsCount ?? d.payments_count ?? 0,
        };
        this.recentContracts = Array.isArray(recentData) ? recentData : [];
        const vehicleRankData = vehicleRankRes.data?.data?.dashboardVehicleRanking;
        const executiveRankData = executiveRankRes.data?.data?.dashboardExecutiveQuoteRanking;
        this.vehicleRanking = Array.isArray(vehicleRankData) ? vehicleRankData : [];
        this.executiveQuoteRanking = Array.isArray(executiveRankData) ? executiveRankData : [];
      } catch (e) {
        this.error = e.response?.data?.errors?.[0]?.message || e.message || 'Error al cargar el dashboard';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      const n = Number(value);
      if (isNaN(n)) return '$0';
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
    },
    getStatusBadgeClass,
    getStatusText,
  },
};
</script>

<style scoped>
.dashboard-main {
  min-height: 100vh;
  background: linear-gradient(160deg, #f0fdf4 0%, #f8fafc 50%, #f0f9ff 100%);
  padding: 1.5rem 2rem 3rem;
}
.dashboard-header {
  margin-bottom: 2rem;
}
.dashboard-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.25rem 0;
}
.dashboard-title .material-icons {
  font-size: 2rem;
  color: #059669;
}
.dashboard-subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 0 0 0.75rem 0;
}
.dashboard-user {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  font-size: 0.9rem;
}
.user-name { font-weight: 600; color: #0f172a; }
.user-email { color: #64748b; }

.dashboard-loading,
.dashboard-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #64748b;
}

.dashboard-section {
  margin-bottom: 2.5rem;
}
.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 1rem 0;
}
.section-title .material-icons {
  font-size: 1.4rem;
  color: #059669;
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.section-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #059669;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}
.section-link:hover { text-decoration: underline; }
.section-link .material-icons { font-size: 1.1rem; }

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}
.kpi-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border-radius: 14px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-icon .material-icons { font-size: 1.6rem; color: #fff; }
.kpi-primary .kpi-icon { background: linear-gradient(135deg, #059669 0%, #10b981 100%); }
.kpi-success .kpi-icon { background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%); }
.kpi-info .kpi-icon { background: linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%); }
.kpi-warning .kpi-icon { background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%); }
.kpi-neutral .kpi-icon { background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%); }
.kpi-contracts .kpi-icon { background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); }
.kpi-active .kpi-icon { background: linear-gradient(135deg, #15803d 0%, #22c55e 100%); }
.kpi-month .kpi-icon { background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%); }
.kpi-expired .kpi-icon { background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%); }
.kpi-expired.has-alert { box-shadow: 0 0 0 2px rgba(239,68,68,0.3); }
.kpi-clients .kpi-icon { background: linear-gradient(135deg, #be185d 0%, #ec4899 100%); }
.kpi-quotes .kpi-icon { background: linear-gradient(135deg, #0e7490 0%, #06b6d4 100%); }
.kpi-vehicles .kpi-icon { background: linear-gradient(135deg, #4f46e5 0%, #818cf8 100%); }

.kpi-content { min-width: 0; }
.kpi-value {
  display: block;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}
.kpi-label {
  display: block;
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.2rem;
}

.rankings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}
.ranking-card {
  background: #fff;
  border-radius: 14px;
  padding: 1.25rem 1.35rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.04);
}
.ranking-hint {
  margin: -0.35rem 0 1rem 0;
  font-size: 0.82rem;
  color: #94a3b8;
}
.ranking-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ranking-item {
  display: grid;
  grid-template-columns: 2rem 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9rem;
}
.ranking-item:last-child {
  border-bottom: none;
}
.rank-position {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 700;
  font-size: 0.8rem;
}
.ranking-item.rank-top .rank-position {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: #fff;
}
.rank-label {
  color: #0f172a;
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-count {
  font-weight: 700;
  color: #059669;
  font-variant-numeric: tabular-nums;
}
.ranking-empty {
  padding: 1.5rem 0;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
}

.recent-table-wrap {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.04);
  overflow: hidden;
}
.recent-table {
  width: 100%;
  border-collapse: collapse;
}
.recent-table th {
  text-align: left;
  padding: 0.85rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.recent-table td {
  padding: 0.85rem 1rem;
  font-size: 0.9rem;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
}
.recent-table tbody tr:hover { background: #f8fafc; }
.recent-table .amount { font-weight: 600; color: #0f172a; }
.recent-table .empty-row { text-align: center; color: #94a3b8; padding: 2rem; }
.btn-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  text-decoration: none;
  padding: 0.35rem;
  border-radius: 8px;
}
.btn-link:hover { background: #f0fdf4; color: #047857; }
.btn-link .material-icons { font-size: 1.2rem; }

@media (max-width: 768px) {
  .dashboard-main { padding: 1rem; }
  .kpi-grid { grid-template-columns: 1fr 1fr; }
  .rankings-grid { grid-template-columns: 1fr; }
  .recent-table-wrap { overflow-x: auto; }
  .recent-table { min-width: 600px; }
}
</style>

<!-- Estilos de badges de estado iguales a la vista de contratos (sin scoped) -->
<style>
.recent-table-wrap .status-badge {
  display: inline-block !important;
  padding: 0.4rem 0.8rem !important;
  border-radius: 0.5rem !important;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
  border: none !important;
  background-color: #9ca3af !important;
  color: white !important;
}
.recent-table-wrap .status-badge.status-activo {
  background-color: #2ECC71 !important;
  color: white !important;
}
.recent-table-wrap .status-badge.status-vencido {
  background-color: #E74C3C !important;
  color: white !important;
}
.recent-table-wrap .status-badge.status-pagado {
  background-color: #3498DB !important;
  color: white !important;
}
.recent-table-wrap .status-badge.status-renovado {
  background-color: #1ABC9C !important;
  color: white !important;
}
.recent-table-wrap .status-badge.status-recuperado {
  background-color: #F39C12 !important;
  color: white !important;
}
.recent-table-wrap .status-badge.status-cancelado {
  background: linear-gradient(135deg, #FF4444 0%, #CC0000 100%) !important;
  color: white !important;
  border: 1px solid #990000 !important;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}
.recent-table-wrap .status-badge.status-default {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%) !important;
  color: white !important;
  border: 1px solid #4b5563 !important;
}
</style>
